import createApiClient from '@/api/axiosInstance';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { beginSearchAction } from '@/js/reduxstore/actions/searchActions';
import { Edit2, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

const PartsDashboardScreen = ({ setScreen }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const { projectId, projectName } = useParams();

  const [project, setProject] = useState(null);
  const [parts, setParts] = useState(null);
  const [selectedParts, setSelectedParts] = useState(new Set());

  const [error, setError] = useState(null);

  const handlePartSelect = (part) => {
    setSelectedParts((prev) => {
      const newSelected = new Set(prev);
      newSelected.has(part) ? newSelected.delete(part) : newSelected.add(part);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    setSelectedParts(
      selectedParts.size === parts.length
        ? new Set()
        : new Set(parts.map((part) => part)),
    );
  };

  useEffect(() => {
    const fetchAllParts = async () => {
      try {
        const response = await apiClient.post('/api/project-part/get', {
          project_id: projectId,
        });
        const responseData = response.data;

        setParts(responseData?.data?.parts);
        setProject(responseData?.data?.project);
      } catch (error) {
        console.error(error?.response?.data?.error_message || error);
      }
    };

    fetchAllParts();
  }, []);

  const handleSave = () => {
    if (selectedParts.size === 0) {
      setError('Atleast select a part to begin search');
      return;
    }

    const partIds = [...selectedParts].map((part) => part.part_id);
    // console.log(partIds);
    dispatch(
      beginSearchAction({
        projectId: projectId,
        projectName: projectName,
        partIds: partIds,
      }),
    );
    navigate(`/project/search/${projectName}/${projectId}`, {
      state: {
        parts: [...selectedParts],
      },
    });
  };

  return (
    <>
      <h2 className="p-3 bg-white mb-4 rounded-lg font-semibold text-xl text-gray-800">
        Project - {projectName}
      </h2>
      <div className="bg-white rounded-lg font-roboto min-h-[calc(100vh-200px)] flex flex-col">
        <div className="py-6 px-8 flex-1 flex flex-col">
          <div className="px-2 py-1 rounded-md flex justify-between items-center mb-8 bg-gray-50">
            <p className="text-purple-700 font-medium text-lg">Parts</p>
            {parts && (
              <button
                onClick={handleSelectAll}
                className="border border-purple-700 text-purple-700 px-3 py-1 rounded-md font-medium hover:bg-veryLightPurple hover:bg-opacity-10 ease-in duration-200"
              >
                {selectedParts.size === parts.length
                  ? 'Unselect All'
                  : 'Select All'}
              </button>
            )}
          </div>

          {parts ? (
            <>
              <div className="grid grid-cols-custom10 gap-4 text-gray-500 text-sm border py-4 rounded-md px-2">
                <div className="col-span-1"></div>
                <div className="col-span-1">Image</div>
                <div className="col-span-1">Part Name</div>
                <div className="col-span-1 text-center">Part Number</div>
                <div className="col-span-1 text-center">Part Revision</div>
                <div className="col-span-1 text-center">Estimated Cost ($)</div>
                <div className="col-span-1 text-center">Quantity</div>
                <div className="col-span-1 text-center">Lead Time</div>
                <div className="col-span-1 text-center">Procurement Intent</div>
                <div className="col-span-1 text-center">Action</div>
              </div>

              {parts.map((part) => (
                <div
                  key={part.id}
                  className="grid grid-cols-custom10 gap-4 mt-2 items-center py-4 text-sm bg-gray-50 rounded-md px-2"
                >
                  <div className="col-span-1">
                    <Input
                      type="checkbox"
                      checked={selectedParts.has(part)}
                      onChange={() => handlePartSelect(part)}
                      className="w-5 h-5 rounded text-lightPurple accent-darkPurple transition-all duration-300 ease-in checked:scale-125"
                    />
                  </div>
                  <div className="col-span-1">
                    <img
                      src={part.image}
                      alt={part.part_name}
                      className="w-12 h-12 bg-gray-100 rounded"
                    />
                  </div>
                  <div className="col-span-1">{part.part_name}</div>
                  <div className="col-span-1 text-center">
                    {part.part_number}
                  </div>
                  <div className="col-span-1 text-center">
                    {part.part_revision}
                  </div>
                  <div className="col-span-1 text-center">
                    ${part.estimated_cost_usd}
                  </div>
                  <div className="col-span-1 text-center">
                    {part.quantity_to_manufacture}
                  </div>
                  <div className="col-span-1 text-center">
                    {part.lead_time_days}
                  </div>
                  <div className="col-span-1 text-center">
                    {part.procurement_intent}
                  </div>
                  <div className="flex gap-2">
                    <button className="w-full flex gap-2 text-purple-700 hover:bg-veryLightPurple hover:bg-opacity-10 ease-in duration-200 px-2 py-1 rounded border border-lightPurple">
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button className="w-full flex gap-2 text-red-700 hover:bg-red-100 ease-in duration-200 px-2 py-1 rounded border border-red-700">
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-600 font-medium text-lg">
              You have no parts in this project, Proceed to add a part
            </div>
          )}

          <div className="flex justify-center gap-4 mt-auto pt-8">
            <Button
              variant="outline"
              className="text-darkPurple border-darkPurple hover:text-darkerPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-200"
              onClick={() => {
                navigate(`/project/add-parts/${projectName}/${projectId}`);
              }}
            >
              {parts ? '+ Add Another Part' : '+ Add Part'}
            </Button>
            {parts && (
              <button
                className="bg-darkPurple text-white px-6 py-1 rounded hover:bg-darkerPurple ease-in duration-200"
                onClick={() => handleSave()}
              >
                Save & Proceed to Manufacturer Search
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PartsDashboardScreen;
